/**
 * The Settings tab of campaign detail. The posting guide (fields, and what
 * suppliers are told) lives in `CampaignPostingGuide.tsx`.
 *
 * Both read from and write to one place — the campaign's `settings` JSON
 * blob — but through separate endpoints, because they are separate concerns
 * and a single "save everything" write is how one screen silently clobbers
 * another's keys. The server merges rather than replaces, so a partial save
 * cannot drop `fields`, suppression lists or HLR config the client never knew
 * about.
 */
import { useEffect, useState } from 'react';
import {
  Button, Divider, Grid, Group, NumberInput, Select, Stack, Switch, Text, TextInput, Textarea,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useCampaignDetail, useEntities, useUpdateCampaign } from '../api/queries';
import { useVocab } from '../vocab';
import { useCrmPermissions } from './crm/crmShared';
import { linkModeOptions } from './people/peopleShared';
import { CampaignSupplierCosts, type CostOverride } from './SupplierCost';
import { rowTypeIsAboutAPerson, showsMoney, useRowTypes } from './streams/streamShared';

const overridesOf = (m: Record<string, number> | null | undefined): CostOverride[] =>
  Object.entries(m ?? {}).map(([supplier_id, amount]) => ({ supplier_id, amount }));

type CampaignDetail = NonNullable<ReturnType<typeof useCampaignDetail>['data']>;

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

export function CampaignSettingsTab({ campaign }: { campaign: CampaignDetail }) {
  const save = useUpdateCampaign(campaign.id);
  const v = useVocab();
  const rowTypes = useRowTypes();
  // Supplier cost is money: shown only on a stream that sells (on or paused).
  const money = showsMoney(campaign.does);
  // Linking is for whoever may link people.
  const seesPeople = useCrmPermissions().peopleLinking;

  const [name, setName] = useState(campaign.name);
  const [description, setDescription] = useState(campaign.description);
  const [rowType, setRowType] = useState(campaign.row_type || 'lead');
  const [active, setActive] = useState(campaign.active);
  const [isRef, setIsRef] = useState(campaign.is_reference_list);
  const [hashFormat, setHashFormat] = useState(campaign.hash_format || 'plain');
  const [maxAttempts, setMaxAttempts] = useState<number | ''>(campaign.settings.max_attempts ?? '');
  const [defaultDelay, setDefaultDelay] = useState<number | ''>(campaign.settings.default_delay ?? '');
  // Unset reads as on, as the API applies it (one day back): showing it
  // off and saving would have switched a live check off.
  const [dedupe, setDedupe] = useState(campaign.settings.dedupe_enabled ?? true);
  // Sent only once switched: an unset check is on for the API and off for
  // imports, and writing either value would change one of them.
  const [dedupeEdited, setDedupeEdited] = useState(false);
  const [lookback, setLookback] = useState<number | ''>(campaign.settings.dedupe_lookback_days ?? '');
  const [requireKey, setRequireKey] = useState(campaign.settings.require_api_key ?? false);
  const [onSuppressionError, setOnSuppressionError] = useState(
    campaign.settings.suppression_on_error ?? 'block',
  );
  const [personLinking, setPersonLinking] = useState(campaign.settings.person_linking ?? 'inherit');
  // Sent only once edited, so a save never clears overrides it did not load.
  const [costs, setCosts] = useState<CostOverride[]>(overridesOf(campaign.settings.supplier_costs));
  const [costsEdited, setCostsEdited] = useState(false);
  const { data: suppliers } = useEntities('suppliers');

  useEffect(() => {
    setName(campaign.name);
    setDescription(campaign.description);
    setRowType(campaign.row_type || 'lead');
    setActive(campaign.active);
    setIsRef(campaign.is_reference_list);
    setHashFormat(campaign.hash_format || 'plain');
    setMaxAttempts(campaign.settings.max_attempts ?? '');
    setDefaultDelay(campaign.settings.default_delay ?? '');
    setDedupe(campaign.settings.dedupe_enabled ?? true);
    setDedupeEdited(false);
    setLookback(campaign.settings.dedupe_lookback_days ?? '');
    setRequireKey(campaign.settings.require_api_key ?? false);
    setOnSuppressionError(campaign.settings.suppression_on_error ?? 'block');
    setPersonLinking(campaign.settings.person_linking ?? 'inherit');
    setCosts(overridesOf(campaign.settings.supplier_costs));
    setCostsEdited(false);
  }, [campaign]);

  async function submit() {
    try {
      await save.mutateAsync({
        name,
        description,
        active,
        is_reference_list: isRef,
        hash_format: hashFormat,
        settings: {
          // `null` means "leave unset" rather than "set to zero": the engine
          // reads an absent key as its own default, and writing 0 for
          // max_attempts would mean a lead is never tried at all.
          max_attempts: maxAttempts === '' ? null : Number(maxAttempts),
          default_delay: defaultDelay === '' ? null : Number(defaultDelay),
          ...(dedupeEdited ? { dedupe_enabled: dedupe } : {}),
          row_type: rowType,
          dedupe_lookback_days: lookback === '' ? null : Number(lookback),
          require_api_key: requireKey,
          suppression_on_error: onSuppressionError,
          // Only sent by someone who can see people, so a save from anyone
          // else leaves it as it was.
          ...(seesPeople ? { person_linking: personLinking } : {}),
          ...(costsEdited
            ? {
                supplier_costs: Object.fromEntries(
                  costs
                    .filter((c) => c.supplier_id && c.amount !== '')
                    .map((c) => [c.supplier_id, Number(c.amount)]),
                ),
              }
            : {}),
        },
      });
      notifications.show({ color: 'teal', title: 'Settings saved', message: `${name} updated.` });
    } catch (e) {
      notifications.show({ color: 'red', title: 'Save failed', message: (e as Error).message });
    }
  }

  return (
    <Stack maw={760}>
      <Grid gutter="sm">
        <Grid.Col span={{ base: 12, sm: 7 }}>
          <TextInput label={`${v.One('campaign')} name`} value={name} onChange={(e) => setName(e.currentTarget.value)} />
        </Grid.Col>
      </Grid>
      <Select
        label="What arrives"
        description={`A label for this ${v.one('campaign')}'s rows, shown in lists. It changes nothing about how they are checked or routed.`}
        data={rowTypes.options}
        value={rowType}
        onChange={(val) => setRowType(val ?? 'lead')}
        allowDeselect={false}
        maw={340}
        data-testid="row-type-select"
      />
      {!rowTypeIsAboutAPerson(rowType) && rowTypeIsAboutAPerson(campaign.row_type) && (
        <Text size="xs" c="dimmed" mt={-8} maw={520} data-testid="row-type-checks-hint">
          {`These ${v.many('lead')} are not about a person. The checks on an email or a phone stay as they are: switch off duplicates below, and suppression or phone checks on each ${v.one('route')}, if they do not apply.`}
        </Text>
      )}
      <Textarea
        label="Description"
        autosize
        minRows={2}
        value={description}
        onChange={(e) => setDescription(e.currentTarget.value)}
      />

      <Divider label="State" labelPosition="left" />
      <Switch
        label={`${v.One('campaign')} is active`}
        description={`Inactive ${v.many('campaign')} reject inbound ${v.many('lead')} outright`}
        checked={active}
        onChange={(e) => setActive(e.currentTarget.checked)}
      />
      <Switch
        label="Reference list"
        description={`Its ${v.many('lead')} are match values for the campaign_match filter, not real ${v.many('lead')}, and are kept out of dashboards and reports`}
        checked={isRef}
        onChange={(e) => setIsRef(e.currentTarget.checked)}
      />

      {/*
        How this campaign's values arrived, when it is used as a suppression
        list. A buyer will not send their customer book in plaintext, so these
        lists come as MD5 — and the engine has to hash each incoming lead the
        same way, or it compares an address against a digest and matches
        nobody. That failure is silent, which is why the setting is here on
        the list rather than assumed at match time.

        It covers the whole campaign, which is the grain LeadByte uses too and
        falls out of keeping one suppression campaign per buyer. The one thing
        it cannot survive is a campaign holding files in two formats: the half
        in the other format matches nobody and looks like a list nobody is on.
        Hence the description saying so rather than leaving it to be found.
      */}
      <Divider label="Suppression matching" labelPosition="left" />
      <Select
        label={`Values in this ${v.one('campaign')} arrived`}
        description={`Applies to every ${v.one('lead')} in this ${v.one('campaign')}, so keep one ${v.one('campaign')} per format: a file hashed differently needs its own. Only used when another ${v.one('destination')} suppresses against this ${v.one('campaign')}.`}
        data={[
          { value: 'plain', label: 'As plain text' },
          { value: 'md5', label: 'MD5 hashed' },
          { value: 'sha256', label: 'SHA-256 hashed' },
        ]}
        value={hashFormat}
        onChange={(v) => setHashFormat(v ?? 'plain')}
        allowDeselect={false}
        maw={340}
      />
      {/*
        What happens when a suppression list can't be checked (the query
        engine down or timing out). Holding back is the default because it
        can be undone: the lead stays and can be redistributed later. A lead
        sent to someone on a do-not-contact list can't be.
      */}
      <Select
        label="If suppression can't be checked"
        description={`Only applies when this ${v.one('campaign')} or its ${v.many('destination')} use suppression lists. Held ${v.many('lead')} can be redistributed once the check is working.`}
        data={[
          { value: 'block', label: `Hold the ${v.one('lead')} back` },
          { value: 'deliver', label: 'Send it unchecked' },
        ]}
        value={onSuppressionError}
        onChange={(val) => setOnSuppressionError(val ?? 'block')}
        allowDeselect={false}
        maw={340}
      />

      <Divider label="Delivery" labelPosition="left" />
      <Grid gutter="sm">
        <Grid.Col span={{ base: 6, sm: 4 }}>
          <NumberInput
            label="Max attempts"
            description="Blank uses the engine default"
            min={1}
            value={maxAttempts}
            onChange={(v) => setMaxAttempts(v === '' ? '' : Number(v))}
          />
        </Grid.Col>
        <Grid.Col span={{ base: 6, sm: 4 }}>
          <NumberInput
            label="Default delay (s)"
            description={`Used when a ${v.one('routeGroup')} sets none`}
            min={0}
            value={defaultDelay}
            onChange={(v) => setDefaultDelay(v === '' ? '' : Number(v))}
          />
        </Grid.Col>
      </Grid>

      {money && (
        <>
          <Divider label={`${v.One('supplier')} costs`} labelPosition="left" />
          <CampaignSupplierCosts
            rows={costs}
            onChange={(next) => { setCosts(next); setCostsEdited(true); }}
            suppliers={suppliers?.entities ?? []}
          />
        </>
      )}

      <Divider label="Duplicates and access" labelPosition="left" />
      <Switch
        label={`Reject duplicate ${v.many('lead')}`}
        description={dedupe && lookback === '' ? 'Looks back 1 day unless set.' : undefined}
        checked={dedupe}
        onChange={(e) => { setDedupe(e.currentTarget.checked); setDedupeEdited(true); }}
      />
      {dedupe && (
        <Grid gutter="sm">
          <Grid.Col span={{ base: 6, sm: 4 }}>
            <NumberInput
              label="Look back (days)"
              min={1}
              value={lookback}
              onChange={(v) => setLookback(v === '' ? '' : Number(v))}
            />
          </Grid.Col>
        </Grid>
      )}
      {seesPeople && (
        <Select
          label={`${v.One('person')} linking`}
          description={isRef
            ? `A reference list is never linked into ${v.many('person')}.`
            : `How this ${v.one('campaign')}'s ${v.many('lead')} join earlier ones from the same ${v.one('person')}.`}
          data={[
            { value: 'inherit', label: "The organisation's setting" },
            ...linkModeOptions(v),
          ]}
          value={isRef ? 'off' : personLinking}
          onChange={(val) => setPersonLinking(val ?? 'inherit')}
          allowDeselect={false}
          disabled={isRef}
          maw={340}
        />
      )}
      <Switch
        label="Require an API key"
        description={`Off means ${v.a('lead')} posted without a key is accepted`}
        checked={requireKey}
        onChange={(e) => setRequireKey(e.currentTarget.checked)}
      />

      <Group justify="flex-end">
        <Button loading={save.isPending} disabled={!name.trim()} onClick={submit}>
          Save settings
        </Button>
      </Group>
    </Stack>
  );
}
